import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../../components/Layout';
import api from '../../services/api';

// Who may see a document. Default and fallback is the most restrictive.
const AUDIENCES: { value: string; label: string; hint: string }[] = [
  { value: 'factory_admin_only', label: 'Factory admin only', hint: 'Only factory admin staff. Not visible in the workstation or dealer apps.' },
  { value: 'employees', label: 'Employees', hint: 'All employees, including the workstation app.' },
  { value: 'dealers', label: 'Dealers', hint: 'Dealer accounts.' },
];
const audienceLabel = (a: string) => AUDIENCES.find(x => x.value === a)?.label || 'Factory admin only';

// Files judged by contents server-side; this just filters the picker.
const UPLOAD_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function DocumentsManagementPage() {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    version: '1.0',
    file_url: '',
    audience: 'factory_admin_only',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsData, catsData] = await Promise.all([
        api.getAllDocuments(),
        api.getDocumentCategories(),
      ]);
      setDocuments(docsData || []);
      setCategories(catsData || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = () => {
    setEditingDocument(null);
    setFile(null);
    setFileError('');
    setFormData({
      title: '',
      description: '',
      category: '',
      version: '1.0',
      file_url: '',
      audience: 'factory_admin_only',
      is_active: true,
    });
    setShowDocumentForm(true);
  };

  const handleEditDocument = (doc: any) => {
    setEditingDocument(doc);
    setFile(null);
    setFileError('');
    setFormData({
      title: doc.title || '',
      description: doc.description || '',
      category: doc.category || '',
      version: doc.version || '1.0',
      file_url: doc.file_url || '',
      audience: doc.audience || 'factory_admin_only',
      is_active: doc.is_active !== undefined ? doc.is_active : true,
    });
    setShowDocumentForm(true);
  };

  const handleSaveDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    if (!editingDocument && !file && !formData.file_url.trim()) {
      setFileError('Attach a PDF or Word file, or provide an external file URL.');
      return;
    }

    setSaving(true);
    try {
      if (editingDocument) {
        // Metadata edit (title, audience, category, etc.). Replacing the file
        // itself is a re-upload as a new document.
        await api.updateDocument(editingDocument.id, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          version: formData.version,
          audience: formData.audience,
          file_url: formData.file_url,
          is_active: formData.is_active,
        });
        alert('Document updated successfully');
      } else if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', formData.title);
        fd.append('description', formData.description);
        fd.append('category', formData.category);
        fd.append('version', formData.version);
        fd.append('audience', formData.audience);
        await api.uploadDocument(fd);
        alert('Document uploaded successfully');
      } else {
        await api.createDocument(formData);
        alert('Document created successfully');
      }

      setShowDocumentForm(false);
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFile = async (doc: any, download: boolean) => {
    try {
      await api.openDocumentFile(doc.id, download);
    } catch (error: any) {
      alert(error.message || 'Failed to open the file');
    }
  };

  const handleDeleteDocument = async (id: string) => {
    if (!confirm('Delete this document? This cannot be undone.')) return;
    try {
      await api.deleteDocument(id);
      alert('Document deleted successfully');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to delete document');
    }
  };

  const filteredDocuments = selectedCategory === 'all'
    ? documents
    : documents.filter(doc => doc.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Documents & SOPs"
        subtitle="Manage standard operating procedures and documentation"
        actions={
          <button
            onClick={handleCreateDocument}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upload Document
          </button>
        }
      />

      {/* Category Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            All Documents
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Documents List */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {selectedCategory === 'all' ? 'All Documents' : selectedCategory}
        </h2>
        {filteredDocuments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No documents found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Title</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Audience</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Version</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Updated</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {doc.title}
                      {doc.original_filename && (
                        <div className="text-xs text-gray-400 font-normal">{doc.original_filename}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.category || '--'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        doc.audience === 'factory_admin_only' ? 'bg-purple-100 text-purple-800'
                        : doc.audience === 'dealers' ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                      }`}>
                        {audienceLabel(doc.audience)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.version || '1.0'}</td>
                    <td className="px-4 py-3 text-sm">
                      {doc.is_active ? (
                        <span className="text-green-600 font-medium">Active</span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(doc.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      {doc.stored_filename ? (
                        <>
                          <button
                            onClick={() => handleOpenFile(doc, false)}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => handleOpenFile(doc, true)}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Download
                          </button>
                        </>
                      ) : doc.file_url ? (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View
                        </a>
                      ) : null}
                      <button
                        onClick={() => handleEditDocument(doc)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Document Form Modal */}
      {showDocumentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingDocument ? 'Edit Document' : 'Upload Document'}
            </h2>
            <form onSubmit={handleSaveDocument} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    <option value="SOPs">SOPs</option>
                    <option value="Policies">Policies</option>
                    <option value="Forms">Forms</option>
                    <option value="Manuals">Manuals</option>
                    <option value="Guidelines">Guidelines</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audience <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.audience}
                  onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {AUDIENCES.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {AUDIENCES.find((a) => a.value === formData.audience)?.hint}
                </p>
              </div>

              {!editingDocument && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File <span className="text-gray-400 font-normal">(PDF or Word — .pdf, .docx, .doc)</span>
                  </label>
                  <input
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setFileError(''); }}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                  />
                  {file && <p className="mt-1 text-sm text-gray-600">{file.name}</p>}
                  {fileError && <p className="mt-1 text-sm text-red-600">{fileError}</p>}
                  <p className="mt-1 text-sm text-gray-500">The file is stored securely and only reachable by someone allowed to see this document. Or leave empty and use an external link below.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{editingDocument ? 'File URL' : 'External file URL (optional)'}</label>
                <input
                  type="url"
                  value={formData.file_url}
                  onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Link to document file (PDF, Google Doc, etc.)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                  Active (visible to its audience)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDocumentForm(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingDocument ? 'Update Document' : 'Upload Document'}
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
